import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ICustomerInvoice, InvoiceStatus} from "../../../../model/api/customers/customer-quikcbook-invoices";
import {billingService} from "../../../../services/api/billing";
import {notifications} from "../../../../services";
import {process} from "@progress/kendo-data-query";

export const useBillingInvoices = (customerId: number, dataState: any) => {
    const [rawInvoices, setRawInvoices] = useState<ICustomerInvoice[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [pendingIds, setPendingIds] = useState<number[]>([]);

    const loadGridData = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await billingService.getCustomerInvoiceList(customerId);
            const sortedData = [...data].sort((a:ICustomerInvoice, b) => {
                return new Date(b.createDate).getTime() - new Date(a.createDate).getTime();
            });

            setRawInvoices(sortedData);
            const inProgress = sortedData.filter((i:ICustomerInvoice) => i.status === InvoiceStatus.InProgress).map(i => i.id);
            setPendingIds(inProgress);
        } catch (error) {
            notifications.error('Failed to load invoices');
        } finally {
            setIsLoading(false);
        }
    }, [customerId]);

    const prevInvoicesRef = React.useRef<ICustomerInvoice[]>([]);

    useEffect(() => {
        if (rawInvoices.length > 0 && prevInvoicesRef.current.length > 0) {
            rawInvoices.forEach(newInvoice => {
                const oldInvoice = prevInvoicesRef.current.find(oi => oi.id === newInvoice.id);

                if (newInvoice.statusDescription && (!oldInvoice || !oldInvoice.statusDescription)) {
                    notifications.info(newInvoice.statusDescription);
                }
            });
        }
        prevInvoicesRef.current = rawInvoices;
    }, [rawInvoices]);

    // load grid data with pooling process
    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (pendingIds.length > 0) {
            timer = setInterval(() => {
                loadGridData();
            }, 5000);
        }

        return () => clearInterval(timer);
    }, [pendingIds, loadGridData]);

    useEffect(() => {
        loadGridData();
    }, [loadGridData]);

    const gridData = useMemo(() => {
        const result = process(rawInvoices, dataState);
        return {
            data: result.data as ICustomerInvoice[],
            total: result.total
        };
    }, [rawInvoices, dataState]);

    return { gridData, isLoading, loadGridData, pendingIds, setPendingIds };
};
